import React from 'react'
import { Card, CardContent, CardMedia, Typography, Stack, Chip, Box, Avatar } from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Listing } from '../types'
import { getListingImage } from '../lib/images'

type ListingCardProps = {
  item: Listing
  fallbackIndex?: number
}

const ListingCard: React.FC<ListingCardProps> = ({ item, fallbackIndex }) => {
  const { t } = useTranslation()
  const imageUrl = getListingImage(item, fallbackIndex)

  return (
    <Card
      sx={{
        height: '100%',
        textDecoration: 'none',
        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 4
        }
      }}
      component={RouterLink as any}
      to={`/listing/${item.id}`}
    >
      {imageUrl && (
        <CardMedia
          component="img"
          image={imageUrl}
          height={200}
          alt={item.title}
          sx={{ objectFit: 'cover' }}
        />
      )}
      <CardContent>
        <Typography variant="h6" fontWeight={600} gutterBottom sx={{
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden'
        }}>
          {item.title}
        </Typography>

        <Typography variant="body2" color="text.secondary" gutterBottom>
          {item.city}{item.district ? ` • ${item.district}` : ''}
        </Typography>

        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Chip
            label={`${item.price}€`}
            size="small"
            color="primary"
            variant="outlined"
          />
          <Chip
            label={`${item.size} m²`}
            size="small"
            color="secondary"
            variant="outlined"
          />
          {item.furnished && (
            <Chip
              label={t('listings.furnished')}
              size="small"
              color="success"
              variant="outlined"
            />
          )}
        </Stack>

        {/* Contact Info */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
            {(item as any).contact?.charAt(0) || '?'}
          </Avatar>
          <Typography variant="caption" color="text.secondary">
            {(item as any).contact || t('listings.contact')}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  )
}

export default ListingCard
